import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  ButtonComponent,
  InputComponent,
  ModalData,
  ModalRef,
  TextAreaComponent,
} from '@ui-lib/components';
import { filter } from 'rxjs';
import { LoadingState } from '../../../../shared/types/loading.type';
import { ServiceCentersService } from '../../service-centers.service';
import {
  IServiceCenterForm,
  IServiceCenterModalData,
  ServiceCenterModalAction,
} from '../../service-centers.model';
import { PermissionService } from '../../../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../../../core/models/user-permissions.enum';

@Component({
  standalone: true,
  selector: 'mon-service-center-modal',
  templateUrl: './service-center-modal.component.html',
  styleUrl: './service-center-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ServiceCentersService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    InputComponent,
    TextAreaComponent,
  ],
})
export class ServiceCenterModalComponent extends ModalData<IServiceCenterModalData> {
  private readonly serviceCentersService = inject(ServiceCentersService);
  private readonly modalRef = inject(ModalRef<boolean>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(PermissionService);

  protected readonly LoadingState = LoadingState;
  protected readonly ServiceCenterModalAction = ServiceCenterModalAction;
  protected readonly serviceCenter = this.modalData.serviceCenter;
  protected readonly canManageMaintenance = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.MaintenanceManage),
    { initialValue: false },
  );

  protected readonly serviceCenterForm = new FormGroup<IServiceCenterForm>({
    name: new FormControl(this.serviceCenter?.name ?? '', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(100)],
    }),
    address: new FormControl(this.serviceCenter?.address ?? '', {
      nonNullable: true,
      validators: [Validators.maxLength(500)],
    }),
    contactInfo: new FormControl(this.serviceCenter?.contactInfo ?? '', {
      nonNullable: true,
      validators: [Validators.maxLength(500)],
    }),
  });

  protected readonly actionLoadingStatus = this.serviceCentersService.actionLoadingStatus;

  protected get titleTranslateKey(): string {
    return this.modalData.action === ServiceCenterModalAction.Create
      ? 'monitoring.service-centers.modal.create-title'
      : 'monitoring.service-centers.modal.edit-title';
  }

  protected onSubmit(): void {
    if (!this.canManageMaintenance()) return;

    if (this.serviceCenterForm.invalid) {
      this.serviceCenterForm.markAllAsTouched();
      return;
    }

    if (this.modalData.action === ServiceCenterModalAction.Edit && !this.serviceCenter?.id) return;

    const formValue = this.serviceCenterForm.getRawValue();
    const request = {
      name: formValue.name.trim(),
      address: formValue.address.trim() || null,
      contactInfo: formValue.contactInfo.trim() || null,
    };

    const action$ =
      this.modalData.action === ServiceCenterModalAction.Create
        ? this.serviceCentersService.createServiceCenter(request)
        : this.serviceCentersService.updateServiceCenter({
            id: this.serviceCenter?.id ?? '',
            name: request.name,
            address: request.address,
            contactInfo: request.contactInfo,
          });

    action$
      .pipe(
        filter((result) => !!result),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => this.closeModal(result));
  }

  protected closeModal(result: boolean): void {
    this.modalRef.close(result);
  }
}
